import React, { useMemo, useState } from 'react'
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { estoqueService } from '../../services/estoque'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import type { EstoqueLocal } from '../../types/estoque'
import EstoqueModal, { EstoqueModalActions } from './EstoqueModal'
import EstoquePagination from './EstoquePagination'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  TEXTAREA_CLASS,
  paginateRows,
  sortRows,
  statusBadgeClass,
} from './estoqueUi'

type Props = {
  locais: EstoqueLocal[]
  loading: boolean
  onChanged: () => void
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
}

const emptyForm = { name: '', description: '', is_active: true }

const EstoqueLocaisTab: React.FC<Props> = ({
  locais,
  loading,
  onChanged,
  canInsert,
  canUpdate,
  canDelete,
}) => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [sortKey, setSortKey] = useState('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<EstoqueLocal | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const sorted = useMemo(
    () =>
      sortRows(locais, sortKey, sortOrder, (row, key) => {
        if (key === 'name') return row.name
        if (key === 'active') return row.is_active ? 1 : 0
        return row.description
      }),
    [locais, sortKey, sortOrder]
  )
  const pageRows = paginateRows(sorted, page)

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (row: EstoqueLocal) => {
    setEditing(row)
    setForm({
      name: row.name,
      description: row.description || '',
      is_active: row.is_active,
    })
    setError(null)
    setModalOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Nome é obrigatório.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (editing) {
        const updated = await estoqueService.updateLocal(editing.id, form)
        void logUpdate(
          'marketing_stock_location',
          updated.id,
          `Atualizou local de estoque: ${updated.name}`,
          {
            name: editing.name,
            description: editing.description,
            is_active: editing.is_active,
          },
          {
            name: updated.name,
            description: updated.description,
            is_active: updated.is_active,
          }
        )
      } else {
        const created = await estoqueService.createLocal(form)
        void logCreate(
          'marketing_stock_location',
          created.id,
          `Criou local de estoque: ${created.name}`,
          {
            name: created.name,
            description: created.description,
            is_active: created.is_active,
          }
        )
      }
      setModalOpen(false)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar local.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: EstoqueLocal) => {
    if (!window.confirm(`Excluir o local "${row.name}"?`)) return
    try {
      const result = await estoqueService.softDeleteLocal(row.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de estoque',
      })
      void logDelete(
        'marketing_stock_location',
        row.id,
        result === 'inactivated'
          ? `Inativou local de estoque com movimentações: ${row.name}`
          : `Excluiu local de estoque: ${row.name}`,
        { name: row.name, inactivated: result === 'inactivated' }
      )
      onChanged()
      if (result === 'inactivated') {
        window.alert('O local possui movimentações e foi inativado.')
      }
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao excluir local.')
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
            Local de Estoque
          </Heading>
          <Text size="sm" color="muted" className="mt-1">
            Cadastre os locais onde o estoque é armazenado.
          </Text>
        </div>
        {canInsert && (
          <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" loadingComponent={PERM_LOADING} fallback={null}>
            <Button variant="solid" onClick={openCreate} className="flex items-center gap-1">
              <PlusIcon className="h-5 w-5" />
              Novo local
            </Button>
          </PermissionGuard>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <SortableTableHeader sortKey="name" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Nome
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Descrição
                  </th>
                  <SortableTableHeader sortKey="active" currentSort={sortKey} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3">
                    Status
                  </SortableTableHeader>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Nenhum local cadastrado.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/20">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{row.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.description || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(row.is_active)}`}>
                          {row.is_active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canUpdate && (
                            <PermissionGuard moduleName={MODULE_NAME} permissionType="update" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => openEdit(row)} className={ICON_BTN_CLASS} title="Editar">
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                          {canDelete && (
                            <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" loadingComponent={PERM_LOADING} fallback={null}>
                              <button type="button" onClick={() => void handleDelete(row)} className={ICON_BTN_DANGER_CLASS} title="Excluir">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EstoquePagination page={page} total={sorted.length} onPageChange={setPage} pageSize={PAGE_SIZE} />

      {modalOpen && (
        <EstoqueModal
          title={editing ? 'Editar local' : 'Novo local'}
          onClose={() => setModalOpen(false)}
          busy={saving}
          onSubmit={handleSave}
          footer={<EstoqueModalActions onCancel={() => setModalOpen(false)} saving={saving} />}
        >
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nome <span className="text-red-500">*</span>
            </label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Descrição</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className={TEXTAREA_CLASS}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Ativo
          </label>
        </EstoqueModal>
      )}
    </div>
  )
}

export default EstoqueLocaisTab
